import React, { useState } from 'react';
import { apiClient, getErrorMessage } from '@/App';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { MessageSquarePlus, Send, Lightbulb } from 'lucide-react';

const FeedbackButton = ({ source = 'app', variant = 'default', className = '' }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const handleSubmit = async () => {
    if (!message.trim()) {
      toast.error('Veuillez rédiger votre suggestion');
      return;
    }

    setSending(true);
    try {
      await apiClient.post('/feedback', {
        message: message.trim(),
        source: source
      });
      toast.success('Merci pour votre suggestion ! 🙏');
      setMessage('');
      setOpen(false);
    } catch (error) {
      const errMsg = getErrorMessage(error, "Erreur lors de l'envoi");
      if (errMsg) toast.error(errMsg);
    } finally {
      setSending(false);
    }
  };

  // Style selon le variant
  const buttonStyles = {
    default: "text-amber-600 hover:text-amber-700 hover:bg-amber-50 border border-amber-200",
    footer: "text-slate-300 hover:text-white hover:bg-slate-700/50",
    assistant: "text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 border border-emerald-200"
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${buttonStyles[variant] || buttonStyles.default} ${className}`}
        data-testid="feedback-button"
      >
        <Lightbulb className="w-4 h-4" />
        <span>Une amélioration à suggérer ?</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="feedback-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquarePlus className="w-5 h-5 text-amber-500" />
              Votre suggestion
            </DialogTitle>
            <DialogDescription>
              Partagez vos idées pour améliorer copropriete.app. Votre feedback sera envoyé à l'équipe de développement.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Décrivez votre suggestion ou amélioration..."
              rows={5}
              className="resize-none"
              data-testid="feedback-textarea"
            />

            <div className="flex gap-3">
              <Button
                onClick={() => setOpen(false)}
                variant="outline"
                className="flex-1"
                disabled={sending}
              >
                Annuler
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!message.trim() || sending}
                className="flex-1 bg-amber-500 hover:bg-amber-600"
                data-testid="send-feedback-button"
              >
                {sending ? (
                  <>Envoi...</>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Envoyer
                  </>
                )}
              </Button>
            </div>

            <p className="text-xs text-slate-400 text-center">
              Envoyé à gmpdeveloppement@gmail.com
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FeedbackButton;
